import {
  PopoverButton as HeadlessPopoverButton,
  PopoverButtonProps as HeadlessPopoverButtonProps,
} from "@headlessui/react"
import { ChevronDown } from "lucide-react"
import {
  ComponentProps,
  cloneElement,
  isValidElement,
  useId,
  RefObject,
} from "react"
import { Label } from "shared-web/components/base/informational/Label"
import { Tag, TagColor } from "shared-web/components/base/informational/Tag"
import { Icon, IconSizes } from "shared-web/components/Icon"

import { classNames } from "lib/classNames"

import { PopoverMultiSelectProps } from "./types"

interface PopoverMultiSelectButtonProps
  extends Omit<HeadlessPopoverButtonProps, "className" | "onSelect">,
    Pick<
      PopoverMultiSelectProps,
      | "selected"
      | "onSelect"
      | "placeholder"
      | "label"
      | "invalid"
      | "errorMessage"
    > {
  className?: string
  containerClassName?: string
  ref?: RefObject<HTMLButtonElement | null>
}

const PopoverMultiSelectButton = (props: PopoverMultiSelectButtonProps) => {
  const {
    ref,
    placeholder,
    selected,
    onSelect,
    label,
    invalid,
    errorMessage,
    className,
    containerClassName,
    disabled,
    ...otherProps
  } = props

  const id = useId()

  return (
    <Label
      label={label}
      inputId={id}
      errorMessage={errorMessage}
      containerClassName={classNames(
        "flex w-full flex-col",
        containerClassName
      )}
    >
      <div
        className={classNames(
          "bg-v2-background-elevation-raised border-v2-border-default color-default text-body shadow-control rounded-control group relative flex min-h-10 w-full items-center gap-x-2 border px-3 py-1.5",
          "hover:not-has-disabled:border-v2-border-strong",
          "has-disabled:border-v2-border-default-disabled has-disabled:bg-v2-background-surface-tonal has-disabled:text-v2-foreground-default-disabled",
          (invalid || !!errorMessage) &&
            "border-v2-border-danger hover:not-has-disabled:border-v2-border-danger",
          className
        )}
      >
        <HeadlessPopoverButton
          // Tags render beside the trigger, not inside it: the trigger swallows Enter/Space, so a nested remove button never fires.
          id={id}
          className="rounded-control absolute -inset-px"
          disabled={disabled}
          {...otherProps}
          ref={ref}
        />
        <div className="pointer-events-none relative flex flex-1 flex-wrap items-center gap-1.5 [&_button]:pointer-events-auto">
          {!selected.length && (
            <span className="text-v2-foreground-default-weaker">
              {placeholder}
            </span>
          )}
          {selected.map((item) => {
            if (
              isValidElement<ComponentProps<typeof Tag>>(item.content) &&
              item.content.type === Tag
            ) {
              return cloneElement(item.content, {
                onDelete: disabled ? undefined : () => onSelect(item),
              })
            }
            return (
              <Tag
                key={item.key}
                name={item.label}
                color={TagColor.Gray}
                onDelete={disabled ? undefined : () => onSelect(item)}
              />
            )
          })}
        </div>
        <Icon
          icon={ChevronDown}
          size={IconSizes.ExtraSmall}
          className="color-icon-subtle group-has-disabled:color-icon-disabled"
        />
      </div>
    </Label>
  )
}

export default PopoverMultiSelectButton
